import {
  addToCart,
  changeCartLine,
  readCartLines,
  type CartLine,
  type CartLines,
  type StorefrontCart,
} from "./cart";

/**
 * 一次加购写完、核对完的结果。数量全部来自 Shopify：quantity 是这一行写完后的件数，
 * added 是这一次实际加进去的件数（库存不够时 Shopify 只加能加的，会比买家要的少）。
 */
export type AddOutcome = (
  | { status: "added"; key?: string; quantity: number; added: number }
  | { status: "rejected"; message?: string; inCart?: number }
  | { status: "failed" }
  /** before 是写入前这一行的件数，之后按它重新核对；写入前没读到购物车就没有。 */
  | { status: "unconfirmed"; before?: number }
) & {
  /** 写入后读到的整车；读不到时没有。 */
  lines?: CartLines;
};

/** 改数量的结果。quantity 是 Shopify 确认的这一行现在的件数。 */
export type ChangeOutcome =
  | { status: "changed"; quantity: number }
  | { status: "rejected"; message?: string; quantity?: number }
  | { status: "failed"; quantity?: number }
  | { status: "missing" }
  | { status: "unconfirmed" };

/**
 * 找挂件加购写进的那一行：优先用 Shopify 给的行 key；没有 key 时找同一变体、
 * 没有 properties 和订阅方案的那一行（商品页带定制信息加的行不算）。
 * key 会随折扣变，自动折扣还会把同一变体拆成几行：key 对不上时，同一变体的这种行恰好一行才认它，
 * 几行并存就认不出，免得改到别的行。
 */
export function findCartLine(
  lines: CartLines,
  variantId: number,
  key?: string,
): CartLine | undefined {
  const exact =
    key === undefined
      ? undefined
      : lines.lines.find((line) => line.key === key);
  if (exact) return exact;
  const plain = lines.lines.filter(
    (line) => line.variantId === variantId && line.plain,
  );
  return key === undefined || plain.length === 1 ? plain[0] : undefined;
}

/** 没拿到写入结果或被拒之后，按写入前后这一行的件数判断：多了就是加上了。 */
function grownSince(
  variantId: number,
  before: number,
  lines: CartLines,
): AddOutcome | undefined {
  const line = findCartLine(lines, variantId);
  const now = line?.quantity ?? 0;
  return now > before
    ? {
        status: "added",
        key: line?.key,
        quantity: now,
        added: now - before,
        lines,
      }
    : undefined;
}

/**
 * 加购并核对：写入前后各读一次购物车，按差值算这一次加了几件。
 * 只有 Shopify 返回成功，或请求超时但前后对比证明确实写进去了，才算加上；
 * 前后对比不出来就是「没法确认」，不能当失败让买家重试，否则可能加两遍。
 */
export async function addAndConfirm(
  variantId: number,
  quantity: number,
  storefront: StorefrontCart,
): Promise<AddOutcome> {
  const before = await readCartLines(storefront);
  const written = await addToCart(variantId, quantity, storefront);
  if (written.isErr() && written.error.status === "failed")
    return {
      status: "failed",
      lines: before.isOk() ? before.value : undefined,
    };
  const after = await readCartLines(storefront);
  const lines = after.isOk() ? after.value : undefined;
  const key = written.isOk() ? written.value.key : undefined;
  const lineIn = (cart: CartLines) =>
    findCartLine(cart, variantId, key)?.quantity ?? 0;
  const previous = before.isOk() ? lineIn(before.value) : undefined;
  if (written.isOk()) {
    const now = lines ? lineIn(lines) : written.value.quantity;
    const diff = previous === undefined ? undefined : now - previous;
    return {
      status: "added",
      key: key ?? (lines && findCartLine(lines, variantId)?.key),
      quantity: now,
      // 前后对不上（期间别处也改了购物车）就按这一行的件数估；都不超过买家要的数。
      added: Math.min(quantity, diff !== undefined && diff > 0 ? diff : now),
      lines,
    };
  }
  // 库存不够时 Shopify 可能先加上能加的件数再回 422（「Only 3 items were added」），车里多了就算加上了。
  const grown =
    previous !== undefined && lines
      ? grownSince(variantId, previous, lines)
      : undefined;
  if (grown) return grown;
  if (written.error.status === "rejected")
    return {
      status: "rejected",
      message: written.error.message,
      inCart: lines && lineIn(lines),
      lines,
    };
  if (previous === undefined || !lines)
    return { status: "unconfirmed", before: previous, lines };
  return { status: "failed", lines };
}

/**
 * 加购结果没法确认之后再核对一次：只读购物车，不再发加购请求，所以不会加两遍。
 * 比写入前多了就是加上了；一样就是没加上，可以放心重试；读不到还是没法确认。
 */
export async function recheckAdd(
  variantId: number,
  before: number,
  storefront: StorefrontCart,
): Promise<AddOutcome> {
  const after = await readCartLines(storefront);
  if (after.isErr()) return { status: "unconfirmed", before };
  return (
    grownSince(variantId, before, after.value) ?? {
      status: "failed",
      lines: after.value,
    }
  );
}

/** 读这一行现在的件数；行不在了（别处删掉了）返回 missing。 */
export async function readLineQuantity(
  variantId: number,
  key: string | undefined,
  storefront: StorefrontCart,
): Promise<
  | { status: "ok"; key: string; quantity: number; currency: string }
  | { status: "missing" }
  | { status: "failed" }
> {
  const lines = await readCartLines(storefront);
  if (lines.isErr()) return { status: "failed" };
  const line = findCartLine(lines.value, variantId, key);
  return line && line.quantity > 0
    ? {
        status: "ok",
        key: line.key,
        quantity: line.quantity,
        currency: lines.value.currency,
      }
    : { status: "missing" };
}

/**
 * 把这一行从 from 件改成 quantity 件并核对。Shopify 返回的整车里这一行的件数才是结果：
 * 库存不够时它可能停在比买家要的少的数。被拒或请求超时就重读一次：件数变了算改成了
 * （Shopify 可能先改成能放的件数再回 422），没变算没改成。
 */
export async function changeAndConfirm(
  variantId: number,
  key: string,
  from: number,
  quantity: number,
  storefront: StorefrontCart,
): Promise<ChangeOutcome> {
  const written = await changeCartLine(key, quantity, storefront);
  if (written.isOk()) {
    const line = findCartLine(written.value, variantId, key);
    return line && line.quantity > 0
      ? { status: "changed", quantity: line.quantity }
      : { status: "missing" };
  }
  const current = await readLineQuantity(variantId, key, storefront);
  if (current.status === "missing") return { status: "missing" };
  const confirmed = current.status === "ok" ? current.quantity : undefined;
  if (written.error.status === "failed")
    return { status: "failed", quantity: confirmed };
  if (confirmed !== undefined && confirmed !== from)
    return { status: "changed", quantity: confirmed };
  if (written.error.status === "rejected")
    return {
      status: "rejected",
      message: written.error.message,
      quantity: confirmed,
    };
  return confirmed === undefined
    ? { status: "unconfirmed" }
    : { status: "failed", quantity: confirmed };
}
